import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useSpotifyPlayer — lazy-loads the Spotify Web Playback SDK and manages
 * the player instance. Returns device id + state + control helpers.
 *
 * This runs an independent audio player in the browser (separate from the
 * Tap IN DSP engine). Spotify audio CANNOT be tapped via WebAudio due to
 * EME/DRM, so the player is a standalone "streaming deck".
 */
const SDK_URL = "https://sdk.scdn.co/spotify-player.js";

export default function useSpotifyPlayer({ enabled, getFreshAccessToken }) {
  const [deviceId, setDeviceId] = useState(null);
  const [state, setState] = useState(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [volume, setVolumeState] = useState(0.8);
  const playerRef = useRef(null);

  // Load the SDK script once
  useEffect(() => {
    if (!enabled) return;
    if (window.Spotify) { /* already loaded */ }
    else if (!document.querySelector(`script[src="${SDK_URL}"]`)) {
      const s = document.createElement("script");
      s.src = SDK_URL; s.async = true;
      document.body.appendChild(s);
    }

    const boot = () => {
      if (playerRef.current) return;
      const P = new window.Spotify.Player({
        name: "Tap IN Radio · DJ Studio",
        getOAuthToken: (cb) => { getFreshAccessToken().then((t) => cb(t || "")); },
        volume,
      });
      P.addListener("ready", ({ device_id }) => { setDeviceId(device_id); setReady(true); });
      P.addListener("not_ready", () => setReady(false));
      P.addListener("player_state_changed", (s) => setState(s));
      P.addListener("initialization_error", ({ message }) => setError(`init · ${message}`));
      P.addListener("authentication_error", ({ message }) => setError(`auth · ${message}`));
      P.addListener("account_error", ({ message }) => setError(`premium required · ${message}`));
      P.addListener("playback_error", ({ message }) => setError(`playback · ${message}`));
      P.connect().then((ok) => { if (!ok) setError("connect failed"); });
      playerRef.current = P;
    };

    if (window.Spotify) boot();
    else window.onSpotifyWebPlaybackSDKReady = boot;

    return () => {
      try { playerRef.current?.disconnect?.(); } catch {}
      playerRef.current = null; setReady(false); setDeviceId(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const play = useCallback(async (uri) => {
    const tok = await getFreshAccessToken();
    if (!tok || !deviceId) return false;
    try {
      const res = await fetch(`https://api.spotify.com/v1/me/player/play?device_id=${deviceId}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${tok}`, "Content-Type": "application/json" },
        body: JSON.stringify(uri ? { uris: [uri] } : {}),
      });
      if (!res.ok && res.status !== 204) {
        const msg = await res.text(); setError(`play ${res.status} · ${msg.slice(0, 120)}`);
        return false;
      }
      return true;
    } catch (e) { setError(`play ${e.message || e}`); return false; }
  }, [deviceId, getFreshAccessToken]);

  const pause = useCallback(async () => { try { await playerRef.current?.pause?.(); } catch {} }, []);
  const resume = useCallback(async () => { try { await playerRef.current?.resume?.(); } catch {} }, []);
  const next = useCallback(async () => { try { await playerRef.current?.nextTrack?.(); } catch {} }, []);
  const prev = useCallback(async () => { try { await playerRef.current?.previousTrack?.(); } catch {} }, []);
  const setVolume = useCallback(async (v) => {
    setVolumeState(v);
    try { await playerRef.current?.setVolume?.(v); } catch {}
  }, []);
  const seek = useCallback(async (ms) => { try { await playerRef.current?.seek?.(ms); } catch {} }, []);

  return { ready, deviceId, state, error, volume, play, pause, resume, next, prev, setVolume, seek };
}
